import { useState, useMemo } from "react";
import { useCourtGroups } from "@/hooks/useCourtGroups";
import { ChevronLeft, Loader2 } from "lucide-react";
import { calculateBookingPrice, type PricingRule, type SpecialPriceDate } from "@/lib/pricing";
import AutoBookingView from "./AutoBookingView";

interface CourtInfo {
  id: string;
  name: string;
  sort_order?: number;
  court_group_id?: string | null;
}

interface BookingInfo {
  id: string;
  court_id: string;
  start_time: string;
  end_time: string;
  status: string;
}

interface CategoryBookingViewProps {
  courts: CourtInfo[];
  bookings: BookingInfo[];
  hallHours: { start: number; end: number };
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  bookingHorizon: number;
  timeStepMinutes: number;
  pricingRules?: PricingRule[];
  specialDates?: SpecialPriceDate[];
  hallId?: string;
  defaultDuration?: number;
  maxDuration?: number;
  categorySlug?: string;
  onBook: (courtIds: string[], startTime: Date, duration: number) => void;
}

export default function CategoryBookingView({
  courts,
  bookings,
  hallHours,
  selectedDate,
  onDateChange,
  bookingHorizon,
  timeStepMinutes,
  pricingRules = [],
  specialDates = [],
  hallId,
  defaultDuration = 60,
  maxDuration = 120,
  categorySlug,
  onBook,
}: CategoryBookingViewProps) {
  const { groups, isLoading } = useCourtGroups(hallId);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // If only 1 group, skip category selection
  const effectiveGroups = groups.filter((g) => {
    const groupCourts = courts.filter((c) => c.court_group_id === g.id);
    return groupCourts.length > 0;
  });

  // Auto-select if only 1 group
  const singleGroup = effectiveGroups.length === 1 ? effectiveGroups[0] : null;
  const activeGroupId = selectedGroupId ?? singleGroup?.id ?? null;

  // Get price per hour for each group (from first court in group)
  const groupPrices = useMemo(() => {
    const prices: Record<string, number | null> = {};
    for (const g of effectiveGroups) {
      if (hallId && pricingRules.length > 0) {
        const price = calculateBookingPrice(pricingRules, hallId, selectedDate, 60, categorySlug ?? "standard", specialDates);
        prices[g.id] = price.perHour;
      } else {
        prices[g.id] = null;
      }
    }
    return prices;
  }, [effectiveGroups, hallId, pricingRules, selectedDate, categorySlug, specialDates]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // If a group is selected (or auto-selected), show auto booking scoped to that group
  if (activeGroupId) {
    const groupCourts = courts.filter((c) => c.court_group_id === activeGroupId);
    return (
      <div className="space-y-3">
        {/* Back to category selection (only if multiple groups) */}
        {!singleGroup && (
          <button
            onClick={() => setSelectedGroupId(null)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" /> Välj kategori
          </button>
        )}
        <AutoBookingView
          courts={groupCourts}
          bookings={bookings}
          hallHours={hallHours}
          selectedDate={selectedDate}
          onDateChange={onDateChange}
          bookingHorizon={bookingHorizon}
          timeStepMinutes={timeStepMinutes}
          pricingRules={pricingRules}
          specialDates={specialDates}
          hallId={hallId}
          defaultDuration={defaultDuration}
          maxDuration={maxDuration}
          categorySlug={categorySlug}
          courtGroupId={activeGroupId}
          onBook={onBook}
        />
      </div>
    );
  }

  // Category selection cards
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">Välj typ av bana</p>
      {effectiveGroups.map((g) => {
        const courtCount = courts.filter((c) => c.court_group_id === g.id).length;
        return (
          <button
            key={g.id}
            onClick={() => setSelectedGroupId(g.id)}
            className="w-full text-left rounded-xl border-2 border-border hover:border-primary/40 p-4 transition-all space-y-1"
          >
            <p className="font-semibold text-sm">{g.display_name}</p>
            {g.description && (
              <p className="text-xs text-muted-foreground">{g.description}</p>
            )}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{courtCount} {courtCount === 1 ? "bana" : "banor"}</span>
              {groupPrices[g.id] != null && (
                <>
                  <span>·</span>
                  <span className="font-medium text-foreground">{groupPrices[g.id]} kr/h</span>
                </>
              )}
            </div>
          </button>
        );
      })}

      {effectiveGroups.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          Inga bankategorier konfigurerade. Kontakta hallen.
        </p>
      )}
    </div>
  );
}
